<script setup>
import {ref, unref, onMounted, reactive, nextTick} from "vue";
import { useUrlParameters } from "@/stores/url_parameters";

import { useAuth } from "@/stores/auth";
import {t} from "i18next";
import Dialog from "@/components/modals/Dialog.vue";
import {handleApiException} from "@/composables/exceptions.js";
import RegistrationAgreementModal from "./RegistrationAgreementModal.vue";
import ConfirmEmailModal from "./ConfirmEmailModal.vue";

const RECAPTCHA_KEY = import.meta.env.VITE_RECAPTCHA_KEY || "6LdAEL0hAAAAAMolqw-WomBkpK4CkO6nq-uPjrEH";
const LOGIN_FORM = "login";
const REGISTRATION_FORM = "registration";

const auth = useAuth();

const modal = ref(null);
const recaptchaElement = ref(null);
const agreementModal = ref(null);
const confirmEmailModal = ref(null);

const state = reactive({
  loading: false,
  currentForm: LOGIN_FORM,
  formError: {},
  user: {
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    password: "",
    password_confirmation: "",
    recaptcha: null,
    url_parameters: null,
  },
  recaptchaWidgetId: null,
});


const showModal = () => {
  modal.value.show();
}

const hideModal = () => {
  // Only trigger hide; actual reset happens in onDialogHide to avoid recursion
  modal.value.hide();
}

const onDialogHide = () => {
  // Reset form state when modal is closed
  state.currentForm = LOGIN_FORM;
  state.formError = {};
  state.recaptchaWidgetId = null;
}

const onRegistrationComplete = (response) => {
  // Registration completed successfully
  console.log('Registration completed successfully', response);
  
  if (response && response.requiresEmailVerification) {
    // Show email confirmation modal with user email
    if (confirmEmailModal.value) {
      confirmEmailModal.value.showModal(response.user.email);
    }
  }
}

const onRegistrationCancelled = () => {
  // User cancelled the registration agreement
  console.log('Registration cancelled by user');
}

const onModalClosed = () => {
  // Close the login modal when agreement modal is closed
  hideModal();
}

const onRegistrationError = (errorMessage) => {
  // Show error in login form when registration fails
  state.formError = { general: [errorMessage] };
  console.error('Registration error:', errorMessage);
}

const login = async () => {
  if (state.currentForm !== LOGIN_FORM) {
    state.currentForm = LOGIN_FORM;
    state.formError = {};
    return;
  }
  if (!state.user.email || !state.user.password) {
    return;
  }
  await loginAction();
}

const loginAction = async () => {
  try {
    state.loading = true;
    await auth.login(state.user.email, state.user.password);
    modal.value.hide();
  }
  catch (e) {
    if (e.response && e.response.data && e.response.data.message) {
      state.formError = {password: [e.response.data.message]};
    }
    handleApiException(e);
  }
  finally {
    state.loading = false;
  }
}

const validateRegistrationForm = () => {
  const errors = {};
  
  // Required fields
  if (!state.user.first_name || state.user.first_name.trim() === '') {
    errors.first_name = ['First name is required'];
  }
  
  if (!state.user.last_name || state.user.last_name.trim() === '') {
    errors.last_name = ['Last name is required'];
  }
  
  if (!state.user.email || state.user.email.trim() === '') {
    errors.email = ['Email is required'];
  } else {
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(state.user.email)) {
      errors.email = ['Email must be a valid email address'];
    }
  }
  
  if (!state.user.password || state.user.password === '') {
    errors.password = ['Password is required'];
  } else if (state.user.password.length < 8) {
    errors.password = ['Password must be at least 8 characters'];
  }
  
  if (state.user.password_confirmation !== state.user.password) {
    errors.password_confirmation = ['Password confirmation does not match'];
  }
  
  return errors;
};

const register = async () => {
  state.formError = {};
  if (state.currentForm !== REGISTRATION_FORM) {
    state.currentForm = REGISTRATION_FORM;
    await renderRecaptcha();
    return;
  }
  
  // Validate form before proceeding
  const validationErrors = validateRegistrationForm();
  if (Object.keys(validationErrors).length > 0) {
    state.formError = validationErrors;
    return;
  }
  try {
    state.loading = true;
    state.user.url_parameters = useUrlParameters().getParameters;

    // Get reCAPTCHA response if available
    if (window.grecaptcha && window.grecaptcha.getResponse && state.recaptchaWidgetId !== null) {
      try {
        state.user.recaptcha = window.grecaptcha.getResponse(state.recaptchaWidgetId);
      } catch (e) {
        console.warn('reCAPTCHA not ready, setting to null:', e);
        state.user.recaptcha = null;
      }
    } else {
      state.user.recaptcha = null;
    }

    // First check if email already exists
    try {
      await auth.prepareRegistration(state.user);
      
      // Email is available, show agreement modal
      state.loading = false;
      
      if (agreementModal.value) {
        await agreementModal.value.showModal();
      } else {
        console.error('Agreement modal not found');
      }
    } catch (e) {
      state.loading = false;
      
      // Handle email already exists error
      if (e.response && e.response.data && e.response.data.errors) {
        state.formError = e.response.data.errors;
      } else if (e.response && e.response.data && e.response.data.message) {
        state.formError = { email: [e.response.data.message] };
      } else {
        state.formError = { email: ['Email is already registered'] };
      }
    }
  } catch (e) {
    state.loading = false;
    
    // Handle other errors
    if (e.response && e.response.data && e.response.data.errors) {
      state.formError = e.response.data.errors;
    }
    handleApiException(e);
  }
}

const renderRecaptcha = async () => {
  try {
    await nextTick();
    if (window.grecaptcha && recaptchaElement.value) {
      // Clear any existing reCAPTCHA first
      if (state.recaptchaWidgetId !== null) {
        try {
          window.grecaptcha.reset(state.recaptchaWidgetId);
        } catch (e) {
          console.warn('Failed to reset reCAPTCHA:', e);
        }
      }
      
      // Render new reCAPTCHA
      state.recaptchaWidgetId = window.grecaptcha.render(recaptchaElement.value, {
        sitekey: RECAPTCHA_KEY,
        theme: "light",
      });
    }
  }
  catch (e) {
    console.error('Rendering recaptcha failed:', e);
  }
}

const onEnterPressed = async () => {
  if (state.currentForm === LOGIN_FORM) {
    await login();
  }
}

const getError = (field) => {
  if (state.formError[field]) {
    return state.formError[field][0];
  }
  return null;
}

const clearFieldError = (field) => {
  if (state.formError[field]) {
    delete state.formError[field];
  }
}

const onFieldInput = (field) => {
  clearFieldError(field);
}

const onFieldBlur = (field) => {
  // Re-validate field when user leaves it empty
  if (state.currentForm === REGISTRATION_FORM) {
    const validationErrors = validateRegistrationForm();
    if (validationErrors[field]) {
      state.formError[field] = validationErrors[field];
    }
  }
}

const unsubscribeFromAuthDialog = auth.$onAction(({
                                                         name, // name of the action
                                                         store, // store instance, same as `someStore`
                                                         args, // array of parameters passed to the action
                                                         after, // hook after the action returns or resolves
                                                         onError,
                                                       }) => {
  if (name === 'showAuthModal') {
    state.currentForm = LOGIN_FORM;
    modal.value.show();
    return;
  }
  if (name === 'showRegisterModal') {
    state.currentForm = REGISTRATION_FORM;
    modal.value.show();
    renderRecaptcha();
    return;
  }
  if (name === 'closeModal') {
    hideModal();
    return;
  }
});

defineExpose({
  showModal,
  hideModal,
});
</script>

<template>
  <Dialog ref="modal" parent-classes="auth-modal" :rounded="false" init-on-mount @hide="onDialogHide">
    <div class="card p-2 rounded-0">
      <div class="card-body">
        <template v-if="state.loading">
          <div>
            <div class="loader position-relative"></div>
            <div class="text-center">{{ t('header.auth.pleaseWait') }}</div>
          </div>
        </template>
        <template v-else>
          <h1 v-show="state.currentForm === LOGIN_FORM" class="text-black py-2 text-center cursor-default">{{ t('header.auth.login') }}</h1>
          <h1 v-show="state.currentForm === REGISTRATION_FORM" class="text-black py-2 text-center cursor-default">{{ t('header.auth.register') }}</h1>
          <div>
            <Transition name="slide-fade">
              <div
                  class="form-group mb-3"
                  v-show="state.currentForm === REGISTRATION_FORM"
              >
                <input
                    v-model="state.user.first_name"
                    type="text"
                    class="form-control rounded-0"
                    autocomplete="given-name"
                    :placeholder="t('modals.login modal.first-name')"
                    required
                    @input="onFieldInput('first_name')"
                    @blur="onFieldBlur('first_name')"
                />
                <div class="text-danger small">{{ getError('first_name') }}</div>
              </div>
            </Transition>
            <Transition name="slide-fade">
              <div
                  class="form-group mb-3"
                  v-show="state.currentForm === REGISTRATION_FORM"
              >
                <input
                    v-model="state.user.last_name"
                    type="text"
                    class="form-control rounded-0"
                    autocomplete="family-name"
                    :placeholder="t('modals.login modal.last-name')"
                    required
                    @input="onFieldInput('last_name')"
                    @blur="onFieldBlur('last_name')"
                />
                <div class="text-danger small">{{ getError('last_name') }}</div>
              </div>
            </Transition>

            <div class="form-group mb-3">
              <input
                  v-model="state.user.email"
                  type="text"
                  autocomplete="email"
                  class="form-control rounded-0"
                  :placeholder="t('modals.login modal.email')"
                  required
                  v-on:keyup.enter="onEnterPressed"
                  @input="onFieldInput('email')"
                  @blur="onFieldBlur('email')"
              />
              <div class="text-danger small">{{ getError('email') }}</div>
            </div>

            <Transition name="slide-fade">
              <div
                  class="form-group mb-3"
                  v-show="state.currentForm === REGISTRATION_FORM"
              >
                <input
                    v-model="state.user.phone"
                    type="text"
                    autocomplete="tel"
                    class="form-control rounded-0"
                    :placeholder="t('modals.login modal.phone')"
                    required
                    @input="onFieldInput('phone')"
                    @blur="onFieldBlur('phone')"
                />
                <div class="text-danger small">{{ getError('phone') }}</div>
              </div>
            </Transition>

            <div class="form-group"
                 :class="{
                'mb-3': state.currentForm === REGISTRATION_FORM,
              }">
              <input
                  v-model="state.user.password"
                  type="password"
                  class="form-control rounded-0"
                  :autocomplete="state.currentForm === REGISTRATION_FORM ? 'new-password': 'current-password'"
                  :placeholder="t('modals.login modal.password')"
                  required
                  v-on:keyup.enter="onEnterPressed"
                  @input="onFieldInput('password')"
                  @blur="onFieldBlur('password')"
              />
              <div class="text-danger small">{{ getError('password') }}</div>
            </div>
            <!--              <div class="text-end" v-show="state.currentForm === LOGIN_FORM">
                            <span class=text-secondary>Forgot your password?</span>
                          </div>-->

            <Transition name="slide-fade">
              <div
                  class="form-group mb-3"
                  v-show="state.currentForm === REGISTRATION_FORM"
              >
                <input
                    v-model="state.user.password_confirmation"
                    type="password"
                    class="form-control rounded-0"
                    autocomplete="new-password"
                    :placeholder="t('modals.login modal.password-confirmation')"
                    required
                    @input="onFieldInput('password_confirmation')"
                    @blur="onFieldBlur('password_confirmation')"
                />
                <div class="text-danger small">{{ getError('password_confirmation') }}</div>
              </div>
            </Transition>

            <div class="justify-content-center mb-3" v-show="state.currentForm === REGISTRATION_FORM">
              <div :id="'registrationRecaptchaElement'+Date.now()" ref="recaptchaElement" />
              <div class="text-danger small">{{ getError('recaptcha') }}</div>
            </div>
          </div>

          <div class="form-group text-center pt-4 mb-4">
            <button
                @click="register"
                class="btn rounded-0 px-4 px-sm-5 me-3"
                :class="{
                'btn-primary': state.currentForm === REGISTRATION_FORM,
                'btn-outline-primary': state.currentForm === LOGIN_FORM,
              }"
            >
              {{ t("modals.login modal.register") }}
            </button>
            <span>or</span>
            <button
                @click="login"
                class="btn rounded-0 px-4 px-sm-5 ms-3 uppercase"
                :class="{
                'btn-primary': state.currentForm === LOGIN_FORM,
                'btn-outline-primary': state.currentForm === REGISTRATION_FORM,
              }"
            >
              {{ t("modals.login modal.login") }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </Dialog>

  <!-- Registration Agreement Modal -->
  <RegistrationAgreementModal
    ref="agreementModal"
    :user-data="state.user"
    @registration-complete="onRegistrationComplete"
    @registration-cancelled="onRegistrationCancelled"
    @modal-closed="onModalClosed"
    @registration-error="onRegistrationError"
  />

  <!-- Email Confirmation Modal -->
  <ConfirmEmailModal
    ref="confirmEmailModal"
  />

</template>